import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';

/**
 * Session-scoped memory for list-page view state — filters, search, sort, page number.
 *
 * List pages held these in plain `useState`, so navigating into a detail page unmounted the
 * list and threw the filter away: a teacher filtering to one grade had to re-filter after every
 * student they opened. `sessionStorage` gives the lifetime we want for free — it survives Back,
 * sidebar re-entry and reload, and dies with the tab, so a filter never outlives the visit.
 *
 * Only view state belongs here. Modal flags, edit targets and form drafts stay on `useState`;
 * restoring those would reopen dialogs on return, which is worse than the bug being fixed.
 */

const STORAGE_KEY = 'risp.listState';

type Store = Record<string, unknown>;

function readStore(): Store {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // A corrupt or hand-edited blob degrades to "no memory", never to a page-level throw.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Store;
  } catch {
    return {};
  }
}

let store: Store = readStore();

function persist(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Private browsing and quota errors must not break a filter click.
  }
}

/** Drop remembered state: one page's keys with a `page:` prefix, or all of it with no argument. */
export function clearListState(prefix?: string): void {
  if (prefix === undefined) {
    store = {};
  } else {
    for (const key of Object.keys(store)) {
      if (key.startsWith(prefix)) delete store[key];
    }
  }
  persist();
}

/** `useState`, but the value is remembered for the rest of the browser tab's session. */
export function useListState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const initialValue = key in store ? (store[key] as T) : initial;
  const [value, setValue] = useState<T>(initialValue);
  const previousValueRef = useRef(initialValue);

  // Writing in an effect rather than inside the setter keeps this safe under StrictMode's
  // double-invoked updaters, and keeps the returned setter exactly `useState`'s.
  useEffect(() => {
    if (value !== previousValueRef.current) {
      store[key] = value;
      persist();
      previousValueRef.current = value;
    }
  }, [key, value]);

  return [value, setValue];
}
